"use client";
import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { IContactForm } from "./ContactFormModal";
import { SelectOption } from "../../search-avaliable/SearchAvailableRoom";
import fuzzysort from "fuzzysort";
import { ComponentSharedDatasourcesReusableLightBoxDatasourcesLabel } from "@/types/generated";
import { Label } from "@/components/atoms/label";
import { CustomSelect } from "@/components/atoms/dropdown";
import { Input } from "@/components/atoms/input";
import { Icon } from "@/components/atoms/icon";

interface IContactFormProps {
  listOption: SelectOption[] | { label: string; options: SelectOption[] }[];
  formLabel: ComponentSharedDatasourcesReusableLightBoxDatasourcesLabel;
}
/**
 * ContactForm Component
 *
 * Renders a dynamic contact form integrated with React Hook Form and custom components.
 * Includes input fields for personal details and a location selector with fuzzy search.
 * Uses form labels and options provided via props for full localization and flexibility.
 */
const ContactForm = ({ listOption, formLabel }: IContactFormProps) => {
  const {
    planning_your_meeting_of_event_text,
    free_quotation_please_fill_text,
    select_location_label,
    first_name_label,
    last_name_label,
    company_name_label,
    email_address_label,
    phone_number_label,
  } = formLabel || {};
  const { control, formState } = useFormContext<IContactForm>();
  const { errors } = formState;

  return (
    <div className="w-full md:pt-6">
      <div className="mb-2.5 text-center">
        <h3 className="text-xl font-[var(--font-semibold)] text-[var(--text-base-primary)]">
          {planning_your_meeting_of_event_text || ""}
        </h3>
        <p className="font-[var(--font-light)] text-[var(--text-gray-normal)]">
          {free_quotation_please_fill_text || ""}
        </p>
      </div>
      {/* Form fields*/}
      <div className="flex flex-col gap-4">
        <div className="w-full">
          {/* Location selection with fuzzy search */}
          <Label className="mb-1.5 font-[var(--font-medium)] text-[var(--text-gray-normal)]">
            {select_location_label || ""}
          </Label>
          <Controller
            name="selectedLocation"
            control={control}
            render={({ field }) => (
              <CustomSelect
                value={field.value}
                options={listOption}
                onChange={field.onChange}
                placeholder={"Hotels"}
                error={errors?.selectedLocation?.value?.message}
                className="w-full"
                size="sm"
                iconCustom={<Icon icon="location-pin" />}
                filterOption={(option, inputValue) => {
                  if (!inputValue) return true;
                  const result = fuzzysort.single(
                    inputValue,
                    option.data.label,
                  );
                  return result !== null;
                }}
                styles={{
                  control: (base) => ({
                    ...base,
                    paddingLeft: "12px",
                    paddingRight: "12px",
                  }),
                  input: (base, _state) => ({
                    ...base,
                    fontSize: "16px",
                  }),
                  groupHeading: (base) => ({
                    ...base,
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "var(--text-gray-stronger)",
                    padding: "8px 12px",
                  }),
                }}
              />
            )}
          />
        </div>

        <div className="flex flex-col gap-2 md:flex-row">
          <div className="w-full">
            <Label className="mb-1.5 font-medium text-[var(--text-gray-normal)]">
              {first_name_label || ""}
            </Label>
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <Input
                  className="w-full rounded-sm border border-gray-300 placeholder:text-sm"
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  error={errors?.firstName?.message}
                  size="sm"
                />
              )}
            />
          </div>
          <div className="w-full">
            <Label className="mb-1.5 font-medium text-[var(--text-gray-normal)]">
              {last_name_label || ""}
            </Label>
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <Input
                  className="w-full rounded-sm border border-gray-300 placeholder:text-sm"
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  error={errors?.lastName?.message}
                  size="sm"
                />
              )}
            />
          </div>
        </div>

        <div>
          <Label className="mb-1.5 font-medium text-[var(--text-gray-normal)]">
            {company_name_label || ""}
          </Label>
          <Controller
            name="companyName"
            control={control}
            render={({ field }) => (
              <Input
                className="w-full rounded-sm border border-gray-300 placeholder:text-sm"
                value={field.value ?? ""}
                error={errors?.companyName?.message}
                onChange={field.onChange}
                size="sm"
              />
            )}
          />
        </div>

        <div className="flex flex-col gap-2 md:flex-row">
          <div className="w-full">
            <Label className="mb-1.5 font-medium text-[var(--text-gray-normal)]">
              {email_address_label || ""}
            </Label>
            <Controller
              name="emailAddress"
              control={control}
              render={({ field }) => (
                <Input
                  className="w-full rounded-sm border border-gray-300 placeholder:text-sm"
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  error={errors?.emailAddress?.message}
                  size="sm"
                />
              )}
            />
          </div>
          <div className="w-full">
            <Label className="mb-1.5 font-medium text-[var(--text-gray-normal)]">
              {phone_number_label || ""}
            </Label>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <Input
                  className="w-full rounded-sm border border-gray-300 placeholder:text-sm"
                  value={field.value ?? ""}
                  error={errors?.phone?.message}
                  onChange={field.onChange}
                  size="sm"
                />
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
